/**
 * Theme System
 *
 * Centralized color tokens for the mSales app.
 * Supports light mode (default) and prepares for dark mode.
 *
 * Usage:
 *   import { theme, getColor } from '../utils/theme';
 *   style={{ color: theme.text.primary }}
 *   style={{ background: getColor('background.main') }}
 */

// =============================================================================
// COLOR PALETTE - Ant Design based
// =============================================================================

export const palette = {
  // Primary Blue
  blue: {
    1: '#e6f7ff',
    2: '#bae7ff',
    3: '#91d5ff',
    4: '#69c0ff',
    5: '#40a9ff',
    6: '#1890ff', // Primary
    7: '#096dd9',
    8: '#0050b3',
    9: '#003a8c',
    10: '#002766',
  },

  // Success Green
  green: {
    1: '#f6ffed',
    2: '#d9f7be',
    3: '#b7eb8f',
    4: '#95de64',
    5: '#73d13d',
    6: '#52c41a', // Primary
    7: '#389e0d',
    8: '#237804',
    9: '#135200',
    10: '#092b00',
  },

  // Warning Gold/Yellow
  gold: {
    1: '#fffbe6',
    2: '#fff1b8',
    3: '#ffe58f',
    4: '#ffd666',
    5: '#ffc53d',
    6: '#faad14', // Primary
    7: '#d48806',
    8: '#ad6800',
    9: '#874d00',
    10: '#613400',
  },

  // Error Red
  red: {
    1: '#fff1f0',
    2: '#ffccc7',
    3: '#ffa39e',
    4: '#ff7875',
    5: '#ff4d4f', // Primary (danger)
    6: '#f5222d',
    7: '#cf1322',
    8: '#a8071a',
    9: '#820014',
    10: '#5c0011',
  },

  // Orange (Canary/Volcano)
  orange: {
    1: '#fff7e6',
    2: '#ffe7ba',
    3: '#ffd591',
    4: '#ffc069',
    5: '#ffa940',
    6: '#fa8c16', // Primary
    7: '#d46b08',
    8: '#ad4e00',
    9: '#873800',
    10: '#612500',
  },

  // Purple
  purple: {
    1: '#f9f0ff',
    2: '#efdbff',
    3: '#d3adf7',
    4: '#b37feb',
    5: '#9254de',
    6: '#722ed1', // Primary
    7: '#531dab',
    8: '#391085',
    9: '#22075e',
    10: '#120338',
  },

  // Cyan
  cyan: {
    1: '#e6fffb',
    2: '#b5f5ec',
    3: '#87e8de',
    4: '#5cdbd3',
    5: '#36cfc9',
    6: '#13c2c2', // Primary
    7: '#08979c',
    8: '#006d75',
    9: '#00474f',
    10: '#002329',
  },

  // Geek Blue
  geekBlue: {
    1: '#f0f5ff',
    2: '#d6e4ff',
    3: '#adc6ff',
    4: '#85a5ff',
    5: '#597ef7',
    6: '#2f54eb', // Primary
    7: '#1d39c4',
    8: '#10239e',
    9: '#061178',
    10: '#030852',
  },

  // Lime
  lime: {
    1: '#fcffe6',
    2: '#f4ffb8',
    3: '#eaff8f',
    4: '#d3f261',
    5: '#bae637',
    6: '#a0d911', // Primary
    7: '#7cb305',
    8: '#5b8c00',
    9: '#3f6600',
    10: '#254000',
  },

  // Magenta/Pink
  magenta: {
    1: '#fff0f6',
    2: '#ffd6e7',
    3: '#ffadd2',
    4: '#ff85c0',
    5: '#f759ab',
    6: '#eb2f96', // Primary
    7: '#c41d7f',
    8: '#9e1068',
    9: '#780650',
    10: '#520339',
  },

  // Neutral Gray
  gray: {
    1: '#ffffff',
    2: '#fafafa',
    3: '#f5f5f5',
    4: '#f0f0f0',
    5: '#d9d9d9',
    6: '#bfbfbf',
    7: '#8c8c8c',
    8: '#595959',
    9: '#434343',
    10: '#262626',
    11: '#1f1f1f',
    12: '#141414',
    13: '#000000',
  },
};

// =============================================================================
// LIGHT THEME (Default)
// =============================================================================

export const lightTheme = {
  // Brand
  brand: {
    primary: palette.blue[6],
    primaryLight: palette.blue[4],
    primaryDark: palette.blue[7],
    primaryBg: palette.blue[1],
  },

  // Semantic Colors
  semantic: {
    success: palette.green[6],
    successLight: palette.green[1],
    successDark: palette.green[7],

    warning: palette.gold[6],
    warningLight: palette.gold[1],
    warningDark: palette.gold[7],

    error: palette.red[5],
    errorLight: palette.red[1],
    errorDark: palette.red[6],

    info: palette.blue[6],
    infoLight: palette.blue[1],
    infoDark: palette.blue[7],
  },

  // Text Colors
  text: {
    primary: 'rgba(0, 0, 0, 0.85)',
    secondary: 'rgba(0, 0, 0, 0.65)',
    tertiary: 'rgba(0, 0, 0, 0.45)',
    disabled: 'rgba(0, 0, 0, 0.25)',
    inverse: '#ffffff',
    link: palette.blue[6],
    linkHover: palette.blue[5],
  },

  // Background Colors
  background: {
    main: palette.gray[3],       // #f5f5f5 - page background
    elevated: palette.gray[1],   // #ffffff - cards, modals
    secondary: palette.gray[2],  // #fafafa - subtle sections
    tertiary: palette.gray[4],   // #f0f0f0 - dividers, hover
    disabled: palette.gray[3],   // #f5f5f5
    highlight: palette.blue[1],  // #e6f7ff - selected row
  },

  // Border Colors
  border: {
    main: palette.gray[5],       // #d9d9d9
    light: palette.gray[4],      // #f0f0f0
    dark: palette.gray[6],       // #bfbfbf
    focus: palette.blue[6],      // #1890ff
  },

  // Shadow
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.03)',
    md: '0 2px 8px rgba(0, 0, 0, 0.06)',
    lg: '0 4px 12px rgba(0, 0, 0, 0.15)',
    xl: '0 8px 24px rgba(0, 0, 0, 0.2)',
  },

  // Special/Status Colors (for specific features)
  status: {
    pending: palette.gold[6],
    approved: palette.green[6],
    rejected: palette.red[5],
    draft: palette.gray[7],
    active: palette.blue[6],
    inactive: palette.gray[6],
    visited: palette.purple[6],
    cart: palette.red[5],
  },

  // Order Type Colors (specific to mSales)
  orderType: {
    RF: palette.blue[6],      // Regular
    SPZ: palette.geekBlue[6], // Special
    KAT: palette.orange[6],   // Catalog
    VIK: palette.green[6],    // Weekend
    TNC: palette.orange[6],   // Technician
    NAC: palette.gold[6],     // National
    ROK: palette.red[6],      // Deadline
    LIS: palette.lime[6],     // List
    PUN: palette.green[6],    // Full
    OST: palette.cyan[6],     // Other
    OS: palette.magenta[6],   // OS
    TLP: palette.purple[6],   // TLP
  },
};

// =============================================================================
// DARK THEME (Prepared for future)
// =============================================================================

export const darkTheme = {
  // Brand
  brand: {
    primary: palette.blue[5],
    primaryLight: palette.blue[4],
    primaryDark: palette.blue[6],
    primaryBg: palette.blue[10],
  },

  // Semantic Colors
  semantic: {
    success: palette.green[5],
    successLight: palette.green[9],
    successDark: palette.green[4],

    warning: palette.gold[5],
    warningLight: palette.gold[9],
    warningDark: palette.gold[4],

    error: palette.red[5],
    errorLight: palette.red[9],
    errorDark: palette.red[4],

    info: palette.blue[5],
    infoLight: palette.blue[9],
    infoDark: palette.blue[4],
  },

  // Text Colors
  text: {
    primary: 'rgba(255, 255, 255, 0.85)',
    secondary: 'rgba(255, 255, 255, 0.65)',
    tertiary: 'rgba(255, 255, 255, 0.45)',
    disabled: 'rgba(255, 255, 255, 0.25)',
    inverse: '#000000',
    link: palette.blue[5],
    linkHover: palette.blue[4],
  },

  // Background Colors
  background: {
    main: palette.gray[12],      // #141414
    elevated: palette.gray[11],  // #1f1f1f
    secondary: palette.gray[10], // #262626
    tertiary: palette.gray[9],   // #434343
    disabled: palette.gray[10],
    highlight: palette.blue[10],
  },

  // Border Colors
  border: {
    main: palette.gray[9],       // #434343
    light: palette.gray[10],     // #262626
    dark: palette.gray[8],       // #595959
    focus: palette.blue[5],
  },

  // Shadow (darker shadows for dark theme)
  shadow: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.2)',
    md: '0 2px 8px rgba(0, 0, 0, 0.3)',
    lg: '0 4px 12px rgba(0, 0, 0, 0.4)',
    xl: '0 8px 24px rgba(0, 0, 0, 0.5)',
  },

  // Status colors stay similar (slightly adjusted)
  status: {
    pending: palette.gold[5],
    approved: palette.green[5],
    rejected: palette.red[5],
    draft: palette.gray[6],
    active: palette.blue[5],
    inactive: palette.gray[7],
    visited: palette.purple[5],
    cart: palette.red[5],
  },

  // Order Type Colors
  orderType: {
    RF: palette.blue[5],
    SPZ: palette.geekBlue[5],
    KAT: palette.orange[5],
    VIK: palette.green[5],
    TNC: palette.orange[5],
    NAC: palette.gold[5],
    ROK: palette.red[5],
    LIS: palette.lime[5],
    PUN: palette.green[5],
    OST: palette.cyan[5],
    OS: palette.magenta[5],
    TLP: palette.purple[5],
  },
};

// =============================================================================
// CANARY THEME OVERRIDES
// =============================================================================

export const canaryOverrides = {
  brand: {
    primary: palette.orange[6],
    primaryLight: palette.orange[4],
    primaryDark: palette.orange[7],
    primaryBg: palette.orange[1],
  },
  text: {
    link: palette.orange[6],
    linkHover: palette.orange[5],
  },
  border: {
    focus: palette.orange[6],
  },
};

// =============================================================================
// THEME EXPORTS & HELPERS
// =============================================================================

// Current theme mode (can be extended to support user preference)
export const isCanaryMode = process.env.REACT_APP_CANARY_MODE === 'true';
export const isDarkMode = false; // Future: process.env.REACT_APP_DARK_MODE === 'true'

// Get base theme
const getBaseTheme = () => (isDarkMode ? darkTheme : lightTheme);

// Apply canary overrides if needed
const applyCanaryOverrides = (baseTheme) => {
  if (!isCanaryMode) return baseTheme;

  return {
    ...baseTheme,
    brand: { ...baseTheme.brand, ...canaryOverrides.brand },
    text: { ...baseTheme.text, ...canaryOverrides.text },
    border: { ...baseTheme.border, ...canaryOverrides.border },
  };
};

// Main theme export
const baseThemeWithOverrides = applyCanaryOverrides(getBaseTheme());

// Add neutral alias for semantic grouping of neutral colors
export const theme = {
  ...baseThemeWithOverrides,
  neutral: {
    bg: baseThemeWithOverrides.background,
    text: baseThemeWithOverrides.text,
    border: baseThemeWithOverrides.border,
  },
};

// Helper to get nested color by path (e.g., 'text.primary', 'semantic.success')
export const getColor = (path) => {
  const keys = path.split('.');
  let result = theme;
  for (const key of keys) {
    result = result?.[key];
    if (result === undefined) return undefined;
  }
  return result;
};

// Helper to get order type color
export const getOrderTypeColor = (code) => {
  return theme.orderType[code] || theme.brand.primary;
};

// Re-export for backwards compatibility with canaryMode.js imports
export const canaryTheme = {
  primary: palette.orange[6],
  primaryLight: palette.orange[4],
  primaryDark: palette.orange[7],
  background: palette.orange[1],
  border: palette.orange[3],
  text: palette.orange[8],
};

export const productionTheme = {
  primary: palette.blue[6],
  primaryLight: palette.blue[4],
  primaryDark: palette.blue[7],
  background: palette.gray[3],
  border: palette.gray[5],
  text: palette.blue[6],
};

export const getCurrentTheme = () => (isCanaryMode ? canaryTheme : productionTheme);
